

import React, { useEffect, useState, useMemo } from 'react';
import { styled } from '@mui/system';
import { DashboardLayout } from 'layouts';
import { BreadCrumb } from 'components';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { AiOutlinePlus, AiOutlineAppstore } from 'react-icons/ai';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Dashboard from 'pages/AnalyticDashboard/Dashboard';
import CombinedAnalyticsDashboard from './CombinedAnalyticsDashboard';

const BUCKET_COLORS = ['#C8102E', '#36333d', '#a2aaad', '#f39c12', '#2c7fb8'];
const MAX_BUCKETS = 5; // must match MultiBucketMixin.MAX_BUCKETS on the backend
const COMBINED_VIEW = '__combined__';

const BucketChipBar = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
  margin: '18px 0 6px 0',
}));

const BucketLabel = styled('span')(() => ({
  fontFamily: 'Roboto',
  fontWeight: 700,
  fontSize: 13,
  color: '#36333D',
  marginRight: 4,
}));

export default function MultiBucketAnalyticsDashboard() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { codes } = useParams();

  const codeList = useMemo(
    () => (codes || '').split(',').map((c) => c.trim()).filter(Boolean),
    [codes]
  );

  // activeView is either one bucket's code, or COMBINED_VIEW
  const [activeView, setActiveView] = useState(codeList[0]);

  // keep the active view valid if the code list changes (e.g. one gets removed)
  useEffect(() => {
    if (codeList.length === 0) return;
    if (activeView !== COMBINED_VIEW && !codeList.includes(activeView)) {
      setActiveView(codeList[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeList.join(',')]);

  const linkData = [
    { displayName: t('XdataAnalyticsDetail.XData'), link: '/x-data' },
    { displayName: t('XdataAnalyticsDetail.Analytics'), link: '/dashboard/xdata/analytics', linkClick: true },
    { displayName: t('XdataAnalyticsDetail.Buckets'), link: '/dashboard/xdata/analytics', linkClick: true },
    { displayName: t('MultiBucketAnalytics.title'), link: '#' },
  ];

  const removeBucket = (code) => {
    const rest = codeList.filter((c) => c !== code);
    if (rest.length === 0) {
      navigate('/dashboard/xdata/analytics');
      return;
    }
    navigate('/dashboard/xdata/analytics/multi/' + rest.join(','), { state: location.state });
  };

  const addBucket = () => {
    navigate('/dashboard/xdata/analytics', {
      state: { reopenMultiPicker: true, preselected: codeList },
    });
  };

  if (codeList.length === 0) {
    return (
      <DashboardLayout>
        <BreadCrumb linkData={linkData} />
        <div style={{ padding: 40, textAlign: 'center', fontFamily: 'Roboto' }}>
          {t('MultiBucketAnalytics.noBucketsSelected')}
        </div>
      </DashboardLayout>
    );
  }

  const chipBar = (
    <BucketChipBar>
      <BucketLabel>
        {t('MultiBucketAnalytics.selectedCount', { count: codeList.length, max: MAX_BUCKETS })}
      </BucketLabel>

      {/* ---- Combined Analysis chip — the new "side button" ---- */}
      <Tooltip title={t('MultiBucketAnalytics.combinedTooltip') || 'View merged analytics across all selected buckets'}>
        <Chip
          icon={<AiOutlineAppstore style={{ fontSize: 14, color: activeView === COMBINED_VIEW ? '#fff' : '#C8102E' }} />}
          label={t('MultiBucketAnalytics.combinedAnalysis') || 'Combined Analysis'}
          onClick={() => setActiveView(COMBINED_VIEW)}
          variant={activeView === COMBINED_VIEW ? 'filled' : 'outlined'}
          style={{
            fontFamily: 'Roboto',
            fontWeight: 700,
            background: activeView === COMBINED_VIEW ? '#36333D' : '#fff',
            color: activeView === COMBINED_VIEW ? '#fff' : '#36333D',
            borderColor: '#36333D',
          }}
        />
      </Tooltip>

      <span style={{ width: 1, height: 22, background: '#E0E0E0', margin: '0 4px' }} />

      {codeList.map((code, idx) => {
        const isActive = code === activeView;
        const color = BUCKET_COLORS[idx % BUCKET_COLORS.length];
        return (
          <Tooltip key={code} title={t('MultiBucketAnalytics.viewBucket', { code }) || code}>
            <Chip
              label={code}
              onClick={() => setActiveView(code)}
              onDelete={() => removeBucket(code)}
              variant={isActive ? 'filled' : 'outlined'}
              style={{
                fontFamily: 'Roboto',
                fontWeight: isActive ? 700 : 400,
                background: isActive ? color : '#fff',
                color: isActive ? '#fff' : '#36333D',
                borderColor: color,
              }}
            />
          </Tooltip>
        );
      })}
      {codeList.length < MAX_BUCKETS && (
        <Chip
          icon={<AiOutlinePlus style={{ fontSize: 14 }} />}
          label={t('MultiBucketAnalytics.addBucket')}
          onClick={addBucket}
          variant="outlined"
          style={{ borderColor: '#C8102E', color: '#C8102E', fontFamily: 'Roboto' }}
        />
      )}
    </BucketChipBar>
  );

  if (activeView === COMBINED_VIEW) {
    return (
      <DashboardLayout>
        <BreadCrumb linkData={linkData} />
        {chipBar}
        <CombinedAnalyticsDashboard
          codeList={codeList}
          dateType={location?.state?.date_type || 'thisyear'}
          customStart={location?.state?.start_date || ''}
          customEnd={location?.state?.end_date || ''}
        />
      </DashboardLayout>
    );
  }

  // key={activeView} forces a full remount of Dashboard when the active
  // bucket changes, so every one of its internal useState/useEffect hooks
  // re-runs clean for the newly selected bucket — exactly as if you had
  // navigated to that bucket's own single-bucket analytics URL directly.
  return (
    <Dashboard
      key={activeView}
      codeOverride={activeView}
      nameOverride={activeView}
      extraHeaderContent={chipBar}
    />
  );
}